import { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { SURVEY_SECTIONS } from "@/constants/survey";

const CHARTABLE = new Set(["multi", "single", "scale"]);

const Bar = ({ label, count, max, total }) => (
  <div className="flex items-center gap-3 text-xs" data-testid="survey-chart-bar">
    <span className="w-44 shrink-0 truncate text-slate-300" title={label}>{label}</span>
    <div className="flex-1 h-5 rounded-full bg-white/5 overflow-hidden">
      <div className="h-full rounded-full bg-sky-400/80 transition-[width] duration-500" style={{ width: `${max ? (count / max) * 100 : 0}%` }} />
    </div>
    <span className="w-16 shrink-0 text-right font-mono text-slate-400">{count} · {total ? Math.round((count / total) * 100) : 0}%</span>
  </div>
);

const Chart = ({ q, rows, shows }) => {
  const counts = useMemo(() => {
    const c = new Map();
    const opts = q.type === "scale" ? Array.from({ length: q.max - q.min + 1 }, (_, i) => String(q.min + i)) : q.source === "shows" ? shows : q.options || [];
    opts.forEach((o) => c.set(o, 0));
    let answered = 0;
    rows.forEach((r) => {
      const v = r.answers[q.id];
      if (v === undefined || v === null || v === "" || (Array.isArray(v) && !v.length)) return;
      answered++;
      (Array.isArray(v) ? v : [v]).forEach((x) => c.set(String(x), (c.get(String(x)) || 0) + 1));
    });
    let entries = [...c.entries()];
    if (q.type !== "scale") entries = entries.filter(([, n]) => n > 0 || (q.options || []).length <= 12).sort((a, b) => b[1] - a[1]);
    return { entries: entries.slice(0, 25), answered, max: Math.max(0, ...entries.map(([, n]) => n)) };
  }, [q, rows, shows]);

  const avg = q.type === "scale" ? (() => { const v = rows.map((r) => r.answers[q.id]).filter((x) => x !== undefined && x !== null && x !== "").map(Number).filter((n) => !Number.isNaN(n)); return v.length ? (v.reduce((a, b) => a + b, 0) / v.length).toFixed(1) : null; })() : null;
  return (
    <div className="card-surface rounded-2xl p-4" data-testid={`survey-chart-${q.id}`}>
      <p className="text-sm font-semibold leading-snug">{q.label}</p>
      <p className="mt-0.5 mb-3 text-[11px] text-slate-500">{counts.answered} answered{avg ? ` · average ${avg}` : ""}{q.type === "multi" ? " · multiple answers allowed" : ""}</p>
      <div className="space-y-1.5">{counts.entries.map(([label, n]) => <Bar key={label} label={label} count={n} max={counts.max} total={counts.answered} />)}</div>
    </div>
  );
};

export const SurveyCharts = ({ rows }) => {
  const [open, setOpen] = useState(true);
  const shows = useMemo(() => [...new Set(rows.flatMap((r) => [...(r.answers.shows_listened || []), r.answers.show_most].filter(Boolean)))].sort(), [rows]);
  if (!rows.length) return null;
  return (
    <div className="mb-8" data-testid="survey-charts">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between text-left mb-3" data-testid="survey-charts-toggle">
        <p className="eyebrow">Results at a glance</p>
        <ChevronDown size={16} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && SURVEY_SECTIONS.map((sec) => (
        <div key={sec.id} className="mb-6">
          <h2 className="font-display text-lg font-extrabold mb-3">{sec.title}</h2>
          <div className="grid lg:grid-cols-2 gap-3">
            {sec.questions.filter((q) => CHARTABLE.has(q.type)).map((q) => <Chart key={q.id} q={q} rows={rows} shows={shows} />)}
          </div>
        </div>
      ))}
    </div>
  );
};
